import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import heroBg from '@/assets/Bg3.png';
import mrnware from '@/assets/mrnware.png';

interface Props {
    children: ReactNode;
    className?: string;
    showWatermark?: boolean;
}

const WATERMARK_COPIES = 6;

function WatermarkRow({ copy }: { copy: number }) {
    return (
        <div className="flex shrink-0 items-center">
            {Array.from({ length: WATERMARK_COPIES }, (_, index) => (
                <img
                    key={`${copy}-${index}`}
                    src={mrnware}
                    alt="MRNware"
                    className="h-[100px] w-auto shrink-0 px-4 opacity-[0.14] sm:h-[120px] sm:px-6 md:h-[176px]"
                />
            ))}
        </div>
    );
}

function MrnwareWatermarkSlider() {
    return (
        <div
            aria-hidden
            dir="ltr"
            className="pointer-events-none absolute inset-x-0 bottom-[4%] z-[1] overflow-hidden"
        >
            <div className="flex w-max will-change-transform animate-mrnware-marquee motion-reduce:animate-none">
                <WatermarkRow copy={0} />
                <WatermarkRow copy={1} />
            </div>
        </div>
    );
}

export default function MainHero({ children, className, showWatermark = true }: Props) {
    return (
        <div
            className={cn(
                'relative z-0 flex w-full justify-start overflow-hidden pt-[100px] md:pt-[160px]',
                'min-h-[560px] sm:min-h-[620px] lg:min-h-[670px]',
                className,
            )}
        >
            {/* Viewport-width layer keeps the bg stable when zooming out */}
            <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
                <img
                    src={heroBg}
                    alt="MRNware"
                    className="absolute left-1/2 top-0 h-full min-h-full w-[100vw] min-w-full max-w-none -translate-x-1/2 object-cover object-top"
                />
            </div>
            {showWatermark ? <MrnwareWatermarkSlider /> : null}

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="relative z-10 w-full mainContainer pb-[130px] md:pb-14"
            >
                {children}
            </motion.div>
        </div>
    );
}
